import { styled } from '@linaria/react';
import { Group, Loader, Select, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import i18n from '../../../base-dictionary';
import { formatDate } from '../../../helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseWhite, secondaryBase, skyLight, skyLightest } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Link } from '../../../ui-library/link/link';
import { Text } from '../../../ui-library/typography/typography';
import { generateJiraSearchUrl } from '../../../utils/jira-utils';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { sortTasks } from './task-detail.helpers';
import { TASK_SORT_BY_OPTIONS, TaskSortBy } from './task-detail.type';
import { TaskMetadataList } from './task-metadata-list';

type TaskListProps = {
  tasks: TaskRecord[];
  title: string;
  onSelectTask: (task: TaskRecord) => void;
  onClose: () => void;
  isLoading: boolean;
  onExpandTaskDetails: (() => void) | undefined;
};

const TaskItem = ({ task, onClick }: { task: TaskRecord; onClick: () => void }) => {
  const shouldTruncate = task.name.length > 50;
  const displayName = shouldTruncate ? `${task.name.slice(0, 50)}...` : task.name;

  const metadata = [
    { label: i18n.t('common.created'), value: formatDate(new Date(task?.created_date)) },
    { label: i18n.t('common.type'), value: task?.type || '-' },
    { label: i18n.t('common.estimation'), value: `${task?.points || '-'}` },
  ];

  return (
    <TaskItemContainer onClick={onClick}>
      <Stack gap={2}>
        <Text weight="bold" size="small" color={secondaryBase}>
          {task.title}
        </Text>

        <Text weight="bold" size="small">
          {displayName}
        </Text>
        <Divider style={{ margin: '8px 0' }} />
        <TaskMetadataList metadata={metadata} />
      </Stack>
    </TaskItemContainer>
  );
};

export const TaskList = ({
  tasks,
  title: _title,
  onSelectTask,
  onClose,
  isLoading,
  onExpandTaskDetails,
}: TaskListProps) => {
  const organization = useGlobalStore((state) => state.organization);
  const [sortBy, setSortBy] = useState<TaskSortBy | null>(TaskSortBy.CREATED_DATE);

  const handleTaskClick = (task: TaskRecord) => {
    onSelectTask(task);
  };

  const sortedTasks = useMemo(() => sortTasks(tasks, sortBy), [tasks, sortBy]);

  return (
    <>
      <Stack gap={5}>
        <Group justify="space-between">
          <Text size="medium" weight="bold">{`${i18n.t('common.work_units')} (${tasks.length})`}</Text>
          <Icon name="close" onClick={onClose} />
        </Group>
        {organization?.uses_jira && (
          <Link href={generateJiraSearchUrl(organization.jira_domain, undefined, tasks)}>Jira Issues</Link>
        )}
        {onExpandTaskDetails && (
          <Button
            size="xs"
            radius="xl"
            variant="secondary"
            onClick={onExpandTaskDetails}
            leftSection={<Icon name="visibility" color={baseWhite} size={16} />}
            disabled={tasks.length === 0}
          >
            Expand {i18n.t('common.work_unit')} Details
          </Button>
        )}
      </Stack>
      <Divider style={{ margin: '16px 0' }} />
      {isLoading ? (
        <LoadingContainer>
          <Loader color={secondaryBase} />
        </LoadingContainer>
      ) : (
        <>
          <Group justify="space-between">
            <Text>Sort by:</Text>
            <Select
              data={TASK_SORT_BY_OPTIONS}
              value={sortBy}
              onChange={(value) => setSortBy(value as TaskSortBy)}
              placeholder="Select sort by"
              size="sm"
              radius="xl"
              clearable
              w={175}
            />
          </Group>
          <Divider style={{ margin: '16px 0' }} />
          <Stack>
            {sortedTasks.map((task) => (
              <TaskItem key={task.id} task={task} onClick={() => handleTaskClick(task)} />
            ))}
          </Stack>
        </>
      )}
    </>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const TaskItemContainer = styled.div`
  background-color: ${skyLightest};
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background-color: ${skyLight};
  }
`;
